export const DEFAULT_PHONE_COUNTRY_CODE = "+91";

function countryDigits(value: unknown): string {
  return String(value ?? "").replace(/\D/g, "") || DEFAULT_PHONE_COUNTRY_CODE.slice(1);
}

/**
 * Convert a local phone input plus its selected dial code to an E.164 number.
 * Explicit international inputs (+44... or 0044...) always take precedence.
 */
export function normalizePhoneNumber(
  value: unknown,
  selectedCountryCode = DEFAULT_PHONE_COUNTRY_CODE,
): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;

  let normalized: string;
  if (raw.startsWith("+")) {
    normalized = `+${digits}`;
  } else if (raw.startsWith("00")) {
    normalized = `+${digits.slice(2)}`;
  } else {
    const dialCode = countryDigits(selectedCountryCode);
    const nationalDigits = digits.replace(/^0+/, "");
    // A pasted country-prefixed number without '+' should not receive the
    // same prefix twice. Local inputs remain governed by the selector.
    if (digits.length > 10 && digits.startsWith(dialCode)) {
      normalized = `+${digits}`;
    } else if (dialCode === "91" && nationalDigits.length > 10) {
      // Indian numbers are 10 digits, so anything longer already carries its
      // own code: "971501778765" became +91971501778765 and was never reached.
      normalized = `+${nationalDigits}`;
    } else {
      const nationalNumber = digits.replace(/^0+/, "");
      normalized = `+${dialCode}${nationalNumber}`;
    }
  }

  return /^\+[1-9]\d{6,14}$/.test(normalized) ? normalized : null;
}

export function phoneNumberFromRow(
  row: Record<string, string>,
  fallback = "",
): string {
  const value = row.phone_number || row.phone || fallback;
  return normalizePhoneNumber(
    value,
    row.phone_number_cc || DEFAULT_PHONE_COUNTRY_CODE,
  ) || "";
}

